<template>
  <div class="custom-upload-estimate container-fluid p-3">
    <!-- Blocks the page for the whole submit/ingest. Every control is also bound to `busy`, so the
         form stays frozen even if this layer is dismissed. -->
    <z-overlay
      v-if="busy && !overlayHidden"
      :notice="parsing ? 'Parsing' : 'Submitting'"
      custom-style="position: fixed;"
      @cancel="overlayHidden = true"
    />
    <h5 class="mb-1">Upload Estimate PDF</h5>
    <p class="text-muted mb-3">
      Upload a Mitchell1 or CCC estimate to create an ADAS inspection on behalf of your customer. Review
      and correct the parsed lines, then submit — the inspection is created in ADAS Map and returns to you
      as an opportunity.
    </p>

    <!-- Issue 622: annotate which ADAS Map shop this upload will create the inspection in. -->
    <div v-if="targetFirmName" class="alert alert-secondary py-2 mb-3">
      <i class="fas fa-store me-1"></i>This upload will create an ADAS inspection for <strong>{{ targetFirmName }}</strong>.
    </div>

    <!-- Firm not provisioned for ADAS Map: instruct the user what to do (spec 5c). -->
    <div v-if="notConfigured" class="alert alert-info">
      <h6 class="mb-1"><i class="fas fa-info-circle me-1"></i>ADAS Map isn't enabled for this location</h6>
      <div>{{ errorMessage }}</div>
    </div>
    <div v-else-if="errorMessage" class="alert alert-warning py-2">{{ errorMessage }}</div>

    <input ref="fileInput" type="file" accept="application/pdf" class="d-none" @change="onFileChange" />

    <!-- Drag & drop + controls (controls sit at the bottom-right of the dropzone; Source/OCR under "Advanced") -->
    <div v-if="!estimate && !parsing" class="mb-3">
      <div
        class="custom-dropzone"
        :class="{ 'custom-dropzone-active': dragActive }"
        @dragover.prevent="dragActive = true"
        @dragenter.prevent="dragActive = true"
        @dragleave.prevent="dragActive = false"
        @drop.prevent="onDrop"
        @click="$refs.fileInput && $refs.fileInput.click()"
      >
        <i class="fas fa-file-pdf custom-dropzone-icon"></i>
        <div v-if="file"><strong>{{ file.name }}</strong> — ready to parse</div>
        <div v-else>Drag &amp; drop a Mitchell1 or CCC PDF here, or click to browse</div>
      </div>
      <div class="d-flex justify-content-end align-items-end gap-2 mt-2 custom-controls">
        <div v-if="showAdvanced" class="d-flex align-items-end gap-2">
          <div>
            <label class="form-label custom-label mb-0">Source</label>
            <select class="form-select form-select-sm" v-model="source" :disabled="busy">
              <option value="">Auto-detect</option>
              <option value="CCC">CCC</option>
              <option value="Mitchell">Mitchell</option>
            </select>
          </div>
          <div>
            <label class="form-label custom-label mb-0">OCR Quality</label>
            <select class="form-select form-select-sm" v-model="quality" :disabled="busy">
              <option value="best">Best (slower)</option>
              <option value="fast">Fast</option>
            </select>
          </div>
        </div>
        <button class="btn btn-link btn-sm text-decoration-none" @click="showAdvanced = !showAdvanced">
          <i class="fas me-1" :class="showAdvanced ? 'fa-chevron-up' : 'fa-sliders-h'"></i>Advanced
        </button>
        <button class="btn btn-primary btn-sm" :disabled="!file || busy" @click="parse">
          <i class="fas fa-magic me-1"></i>{{ parsing ? 'Parsing…' : 'Parse' }}
        </button>
        <button v-if="file && !parsing" class="btn btn-outline-secondary btn-sm" :disabled="busy" @click="reset">Clear</button>
      </div>
    </div>

    <div v-if="parsing" class="text-muted mb-3"><span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Reading and parsing the document…</div>
    <!-- An unqualified "submitted" leaves the provider with nothing to check against, so the created
         inspection is named here and the review page is one click away. -->
    <div v-if="submittedOk" class="alert alert-success py-2">
      Estimate submitted.
      <template v-if="submitted && submitted.adasInspection">
        ADAS inspection <strong>{{ submitted.adasInspection }}</strong> was created.
      </template>
      <!-- An inspection that needs no calibration raises no opportunity. Saying so here is the whole
           point: otherwise the provider waits for one that is never coming. -->
      <template v-if="submitted && submitted.numFound === 0">
        The scrub found nothing requiring calibration, so no opportunity will be raised.
      </template>
      <template v-else>
        The inspection will appear as an opportunity shortly.
      </template>
      <router-link to="/upload-estimate-review" class="ms-1">Estimate Upload Review</router-link>
    </div>

    <!-- Parse quality banner + the Cancel/Submit actions. The actions sit at the TOP of the review so
         they are reachable without scrolling past the whole line grid. -->
    <div v-if="estimate" class="row align-items-center mb-3">
      <div class="col">
        <div
          class="alert py-2 mb-0"
          :class="parseAlertClass"
          title="A heuristic for how cleanly the rows landed in the expected columns. It is not a measure of field accuracy, so check the lines."
        >
          <strong>Detected:</strong> {{ platformDisplay }} &nbsp;|&nbsp;
          <strong>Processing:</strong> {{ processingLabel }} &nbsp;|&nbsp;
          <strong>Parse quality:</strong> {{ qualityLabel }} &nbsp;|&nbsp;
          <strong>Lines:</strong> {{ lines.length }}
        </div>
      </div>
      <div class="col-auto text-end">
        <button class="btn btn-secondary btn-sm" :disabled="busy" @click="reset">Cancel</button>
        <button class="btn btn-success btn-sm ms-1" :disabled="busy || !canConfirm" @click="openSummary">
          <i class="fas fa-check me-1"></i>{{ busy ? 'Please wait…' : 'Submit' }}
        </button>
      </div>
    </div>

    <!-- Side-by-side review -->
    <div v-if="estimate" class="row">
      <div class="custom-split">
      <!-- v-show, not v-if, so collapsing the pane does not tear down the embed and force the blob
           to reload when it comes back. -->
      <div class="custom-split-pane mb-3" v-show="pdfOpen">
        <div class="fw-bold mb-1">
          Original PDF
          <a v-if="pdfUrl" :href="pdfUrl" target="_blank" class="custom-pane-link">
            <i class="fas fa-external-link-alt"></i> open in a new tab
          </a>
          <a href="#" class="custom-pane-link me-3" title="Collapse the PDF and widen the parsed estimate"
            @click.prevent="pdfOpen = false">
            <i class="fas fa-chevron-left"></i> hide
          </a>
        </div>
        <!-- v-show, not v-if, so the embed keeps its blob URL and does not reload. The browser's PDF
             plugin paints in a native layer above page content regardless of z-index, so it would
             cover the submit overlay; display:none is what actually removes that layer. -->
        <object v-if="pdfUrl" v-show="!busy" :data="pdfUrl" type="application/pdf" class="custom-pdf-frame">
          <p class="p-3">Unable to display the PDF inline. <a :href="pdfUrl" target="_blank">Open in a new tab</a>.</p>
        </object>
        <div v-if="pdfUrl && busy" class="custom-pdf-placeholder">
          <i class="fas fa-file-pdf me-2"></i>Hidden while the estimate is submitted.
        </div>
      </div>
      <div class="custom-split-pane mb-3">
        <!-- Above the title, not beside it: with the PDF collapsed this pane becomes the left-hand
             column, so the way back belongs at its top-left. -->
        <div v-if="!pdfOpen" class="custom-pane-restore">
          <a href="#" title="Show the PDF again" @click.prevent="pdfOpen = true">
            <i class="fas fa-chevron-right"></i> show PDF
          </a>
        </div>
        <div class="fw-bold mb-1">Parsed Estimate (editable)</div>
        <div class="custom-review-pane">
        <CustomerShopSection v-model="shop" :shops="shops" :disabled="busy"
          :name-missing="shopNameMissing" :contact-missing="shopContactMissing" />

        <!-- Vehicle and estimate header, collapsible like the sections below it. Sits under Customer
             Shop because the shop is the one thing nothing upstream fills in. -->
        <div class="custom-vehicle-section">
          <button type="button" class="custom-section-header" @click="vehicleOpen = !vehicleOpen">
            <i class="fas" :class="vehicleOpen ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
            Vehicle Information<span v-if="vehicleSummary" class="fw-normal"> ({{ vehicleSummary }})</span>
            <span class="custom-section-hint">{{ vehicleOpen ? 'click to collapse' : 'click to expand' }}</span>
          </button>
          <div v-show="vehicleOpen" class="custom-vehicle-body">
          <!-- Every control below is bound to `busy`, so the whole review freezes once Submit is pressed. -->
          <div v-if="yearUnsupported" class="alert alert-warning py-2 mb-2">
            <strong>{{ estimate.year }} vehicles are not supported.</strong>
            ADAS inspections are only created for 2013 and newer, so this estimate cannot be submitted.
          </div>
          <div class="row g-2">
            <div class="col-6">
              <label class="custom-label">VIN</label>
              <input class="form-control form-control-sm" :class="{ 'is-invalid': vinCheckFailed }" v-model="estimate.vin" maxlength="17" :disabled="busy" />
              <!-- A warning, not a block: some non-North American VINs legitimately fail the check. -->
              <div v-if="vinCheckFailed" class="custom-field-error">{{ vinCheckMessage }}</div>
            </div>
            <div class="col-6">
              <label class="custom-label">RO Number <span class="text-danger">*</span></label>
              <input
                class="form-control form-control-sm"
                :class="{ 'is-invalid': roMissing || roDuplicate }"
                v-model="estimate.repairOrderNumber"
                :disabled="busy"
                @input="onRoInput"
                @change="checkRo"
              />
              <div v-if="roMissing" class="custom-field-error">Required for provider submitted inspections</div>
              <div v-else-if="roDuplicate" class="custom-field-error">{{ roDuplicateMessage }}</div>
              <div v-else-if="roChecking" class="custom-field-note">checking RO number…</div>
            </div>
            <div class="col-3">
              <label class="custom-label">Year</label>
              <input class="form-control form-control-sm" :class="{ 'is-invalid': yearUnsupported }" v-model="estimate.year" :disabled="busy" />
            </div>
            <div class="col-3"><label class="custom-label">Make</label><input class="form-control form-control-sm" v-model="estimate.make" :disabled="busy" /></div>
            <div class="col-6"><label class="custom-label">Model</label><input class="form-control form-control-sm" v-model="estimate.model" :disabled="busy" /></div>
            <!-- Issue 622: insurers come from the resolved target ADAS Map environment; "enter manually"
                 flips to free text for anything not in the list. -->
            <div class="col-8">
              <label class="custom-label">
                Insurance
                <span class="ms-2 text-muted"><input type="checkbox" v-model="insuranceOverride" :disabled="busy" @change="onInsuranceOverrideToggle" /> enter manually</span>
              </label>
              <select v-if="!insuranceOverride && insurers.length" class="form-select form-select-sm" v-model="estimate.insuranceCompany" :disabled="busy">
                <option value="">(select insurer)</option>
                <option v-for="ins in insurers" :key="ins.value" :value="ins.name">{{ ins.name }}</option>
              </select>
              <input v-else class="form-control form-control-sm" v-model="estimate.insuranceCompany" placeholder="insurer name" :disabled="busy" />
            </div>
          </div>
          </div>
        </div>

        <div class="custom-lines-section">
        <button type="button" class="custom-section-header" @click="linesOpen = !linesOpen">
          <i class="fas" :class="linesOpen ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
          Line Items ({{ lines.length }})
          <span class="custom-section-hint">{{ linesOpen ? 'click to collapse' : 'click to expand' }}</span>
        </button>
        <div v-show="linesOpen" class="custom-lines-scroll">
        <table class="table table-bordered table-striped mb-0 custom-lines">
          <thead>
            <tr>
              <th class="col-num">#</th>
              <!-- The supplement that added the line (S01/E01). Editable, because a reviewer who adds a
                   line by hand has to be able to say which supplement it belongs to. -->
              <th class="col-supp">Supp</th>
              <th class="col-group">Group</th>
              <th class="col-desc">Description</th>
              <th class="col-oper">Oper</th>
              <th class="col-part">Part #</th>
              <th class="col-qty">Qty</th>
              <th class="col-price">Price</th>
              <th class="col-hrs">Labor Hrs</th>
              <th class="col-hrs">Paint Hrs</th>
              <th class="col-rm"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(l, i) in lines" :key="i" :class="{ 'table-warning': (l.confidence || 1) < 0.4 }">
              <!-- The line number as PRINTED on the estimate, so the grid lines up with the PDF
                   beside it. Manually added lines fall back to the internal sequence. -->
              <td class="col-num text-muted">{{ l.sourceLineNum || (l.lineNum != null ? l.lineNum : i) }}</td>
              <td class="col-supp"><input class="form-control form-control-sm" v-model="l.supplementCode" placeholder="-" :disabled="busy" /></td>
              <td class="col-group"><input class="form-control form-control-sm" v-model="l.lineHeaderDesc" :title="l.lineHeaderDesc" :disabled="busy" /></td>
              <td class="col-desc"><input class="form-control form-control-sm" v-model="l.lineDesc" :title="l.lineDesc" :disabled="busy" /></td>
              <td class="col-oper"><input class="form-control form-control-sm" v-model="l.laborOperation" :disabled="busy" /></td>
              <td class="col-part"><input class="form-control form-control-sm" v-model="l.partNumber" :disabled="busy" /></td>
              <td class="col-qty"><input class="form-control form-control-sm text-end" type="number" step="any" v-model.number="l.quantity" :disabled="busy" /></td>
              <td class="col-price"><input class="form-control form-control-sm text-end" type="number" step="any" v-model.number="l.price" :disabled="busy" /></td>
              <!-- Blank stays blank: an empty cell means the estimate did not state hours, which is not
                   the same as zero. "Incl." shows as a placeholder when the source said so. -->
              <td class="col-hrs"><input class="form-control form-control-sm text-end" type="number" step="any" :placeholder="l.laborIncluded ? 'Incl.' : ''" v-model.number="l.laborHours" :disabled="busy" /></td>
              <td class="col-hrs"><input class="form-control form-control-sm text-end" type="number" step="any" v-model.number="l.paintHours" :disabled="busy" /></td>
              <td class="col-rm text-center"><button class="btn btn-link btn-sm text-danger p-0" :disabled="busy" @click="removeLine(i)"><i class="fas fa-times"></i></button></td>
            </tr>
            <tr v-if="!lines.length"><td colspan="11" class="text-center text-muted">No lines parsed.</td></tr>
          </tbody>
        </table>
        </div>
        </div>
        <div class="custom-review-foot">
          <button class="btn btn-outline-secondary btn-sm" :disabled="busy" @click="addLine"><i class="fas fa-plus me-1"></i>Add line</button>

          <div v-if="warnings.length" class="mt-2">
            <button type="button" class="btn btn-link btn-sm p-0" @click="notesOpen = !notesOpen">
              <i class="fas" :class="notesOpen ? 'fa-chevron-down' : 'fa-chevron-right'"></i> Notes ({{ warnings.length }})
            </button>
            <ul v-show="notesOpen" class="custom-notes mb-0"><li v-for="(w, i) in warnings" :key="i">{{ w.message }}</li></ul>
          </div>
        </div>
        </div>
      </div>
      </div>
    </div>

    <!-- Last look before anything is created. The ingest is one-way - it creates an inspection in ADAS
         Map and pushes an opportunity - so a typo is worth catching here rather than afterwards. -->
    <ZDialog :show="showSummary" modal-size="modal-lg">
      <template #header>
        <h5 class="modal-title">Confirm submission</h5>
      </template>
      <template #body>
        <p class="mb-3">This will create an ADAS inspection for the following. Check it before submitting.</p>
        <dl class="row custom-summary mb-0">
          <dt class="col-4">Uploading to</dt>
          <dd class="col-8">{{ targetFirmName || '-' }}</dd>

          <dt class="col-4">Customer shop</dt>
          <dd class="col-8">
            {{ shop.shopName || '-' }}
            <span v-if="shop.contactName" class="text-muted"> ({{ shop.contactName }})</span>
            <div v-if="shop.email" class="text-muted small">{{ shop.email }}</div>
            <div v-if="shop.phone" class="text-muted small">{{ shop.phone }}</div>
          </dd>

          <dt class="col-4">RO Number</dt>
          <dd class="col-8"><strong>{{ (estimate && estimate.repairOrderNumber) || '-' }}</strong></dd>

          <dt class="col-4">VIN</dt>
          <dd class="col-8">
            {{ (estimate && estimate.vin) || '-' }}
            <div v-if="vinCheckFailed" class="custom-field-error">
              {{ vinCheckMessage }}
              <label class="d-block mt-1 text-body"><input type="checkbox" v-model="vinConfirmed" :disabled="busy" /> The VIN is correct, submit it anyway</label>
            </div>
          </dd>

          <dt class="col-4">Vehicle</dt>
          <dd class="col-8">{{ vehicleSummary || '-' }}</dd>

          <dt class="col-4">Insurance</dt>
          <dd class="col-8">{{ (estimate && estimate.insuranceCompany) || '(none)' }}</dd>

          <dt class="col-4">Source</dt>
          <dd class="col-8">{{ platformDisplay }}</dd>

          <dt class="col-4">Line items</dt>
          <dd class="col-8">{{ lines.length }}</dd>
        </dl>

        <div v-if="busy" class="text-muted small mt-3">
          <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          Submitting and generating the suggested calibrations. This may take a moment.
        </div>
      </template>
      <template #footer>
        <button class="btn btn-secondary btn-sm" :disabled="busy" @click="showSummary = false">Go back</button>
        <button class="btn btn-success btn-sm ms-1" :disabled="busy || (vinCheckFailed && !vinConfirmed)" @click="confirmSubmission">
          <i class="fas fa-check me-1"></i>{{ busy ? 'Please wait…' : 'Confirm and submit' }}
        </button>
      </template>
    </ZDialog>
  </div>
</template>
<script>
import { mapActions } from 'pinia'
import { estimateStore } from '@/stores/estimate'
import { customerStore } from '@/stores/customer'
import ZOverlay from '@/components/utilities/elements/Overlay.vue'
import ZDialog from '@/components/utilities/elements/Dialog.vue'
import { vinCheckDigit } from '@/plugins/vin'
import CustomerShopSection, { emptyShop } from '@/components/opportunity/forms/CustomerShopSection.vue'
export default {
  name: 'UploadEstimateView',
  components: {
    CustomerShopSection,
    ZOverlay,
    ZDialog
  },
  data() {
    return {
      file: null, source: '', quality: 'best',
      busy: false, parsing: false, dragActive: false, submittedOk: false, submitted: null, errorMessage: '', notConfigured: false, showAdvanced: false,
      blobKey: null, fileName: null, contentType: null, byteSize: null,
      estimate: null, lines: [], pdfUrl: null, notesOpen: false,
      // Collapsing the PDF pane hands its width to the parsed estimate, which is the wide one.
      pdfOpen: true,
      // The line grid renders at full height, so it is collapsible.
      linesOpen: true,
      // RO duplicate check against the target ADAS Map shop.
      roChecking: false,
      roDuplicate: false,
      roDuplicateMessage: '',
      // Bumped per RO check so a slow answer for an old value cannot overwrite a newer one.
      roCheckSeq: 0,
      // Ticked in the confirm dialog to accept a VIN that fails the check digit.
      vinConfirmed: false,
      vehicleOpen: true,
      showSummary: false,
      // The customer shop this estimate belongs to, and the provider's saved shops for autocomplete.
      shop: emptyShop(),
      shops: [],
      // Matches [RequestSizeLimit] on the API's parse endpoint.
      maxUploadMb: 30,
      // Set if the user dismisses the blocking layer on a slow request; the form stays disabled.
      overlayHidden: false,
      // Issue 622: resolved target shop (for the banner) + that env's insurer list.
      targetFirmName: '', insurers: [], insuranceOverride: false
    }
  },
  async created() {
    // Issue 622: resolve where this upload lands + load that environment's insurer list.
    try {
      const t = await this.getTarget()
      this.targetFirmName = (t && t.firmName) || ''
      this.insurers = (t && t.insurers) || []
    } catch (e) {
      // Non-fatal: the banner just won't show and Insurance falls back to free text.
      this.insurers = []
    }
    try {
      this.shops = await this.getShops()
    } catch (e) {
      // Non-fatal: the shop fields still work as free text.
      this.shops = []
    }
  },
  mounted() {
  },
  beforeUnmount() {
    this.setPreviewOpen(false)
  },
  watch: {
    // The side-nav layout hides the menu only while a parsed preview is open.
    estimate(value) {
      this.setPreviewOpen(!!value)
    },
    'estimate.vin'() {
      this.vinConfirmed = false
    }
  },
  computed: {
    // 17 characters that fail the check digit (or use I, O, Q). Other lengths are left alone.
    vinCheckFailed() {
      const v = String((this.estimate && this.estimate.vin) || '').trim().toUpperCase()
      return v.length === 17 && vinCheckDigit(v) !== v[8]
    },
    vinCheckMessage() {
      return vinCheckDigit(this.estimate && this.estimate.vin) === null
        ? 'VIN contains invalid characters (I, O and Q are not used in VINs). Check it before submitting.'
        : 'VIN check digit (9th character) does not match, so the VIN may have a typo. Check it before submitting.'
    },
    warnings() { return (this.estimate && this.estimate.warnings) || [] },
    // Shown beside the heading so the vehicle is still identifiable while the section is collapsed.
    vehicleSummary() {
      if (!this.estimate) return ''
      return [this.estimate.year, this.estimate.make, this.estimate.model]
        .filter((part) => !!part)
        .join(' ')
    },
    // Issue 622: parse-quality banner (mirrors ADAS Map).
    parseConfidence() { return this.estimate ? (this.estimate.confidence || 0) : 0 },
    parseAlertClass() {
      const c = this.parseConfidence
      return c >= 0.75 ? 'alert-success' : (c >= 0.4 ? 'alert-warning' : 'alert-danger')
    },
    platformDisplay() {
      const p = this.estimate && this.estimate.platform
      if (!p) return '—'
      return p.toLowerCase() === 'ccc' ? 'CCC' : p
    },
    processingLabel() {
      return (this.estimate && this.estimate.processingMethod) === 'OCR' ? 'OCR (scanned document)' : 'Text-based'
    },
    qualityLabel() {
      const c = this.parseConfidence
      return c >= 0.75 ? 'High' : (c >= 0.4 ? 'Medium' : 'Review Carefully')
    },
    // RO is mandatory: ADAS Map will not push an Opportunity to a provider without one.
    roMissing() {
      return !!this.estimate && !String(this.estimate.repairOrderNumber || '').trim()
    },
    // The shop is how an inspection uploaded on a customer's behalf stays traceable, and Email Customer
    // needs a way to reach them, so a name plus one contact route are the minimum.
    // Mirrors the API: ingestion will not capture a pre-2013 vehicle, so stop it here rather than let
    // the upload look like it worked.
    yearUnsupported() {
      if (!this.estimate) return false
      let y = String(this.estimate.year || '').trim()
      if (!y) return false
      if (y.length === 2) y = `20${y}`
      const n = parseInt(y, 10)
      return !isNaN(n) && n > 0 && n < 2013
    },
    shopNameMissing() {
      return !!this.estimate && !String(this.shop.shopName || '').trim()
    },
    shopContactMissing() {
      if (!this.estimate || this.shopNameMissing) return false
      return !String(this.shop.email || '').trim() && !String(this.shop.phone || '').trim()
    },
    canConfirm() {
      return !!(
        this.estimate &&
        this.estimate.vin &&
        this.lines.length > 0 &&
        !this.roMissing &&
        !this.roDuplicate &&
        !this.shopNameMissing &&
        !this.shopContactMissing &&
        !this.yearUnsupported
      )
    }
  },
  methods: {
    ...mapActions(estimateStore, ['parsePdf', 'confirmPdf', 'getTarget', 'checkRoNumber', 'setPreviewOpen']),
    // Editing the RO drops the previous duplicate verdict at once, so Submit is usable again and
    // re-checks the new value.
    onRoInput() {
      if (this.roDuplicate && this.errorMessage === this.roDuplicateMessage) this.errorMessage = ''
      this.roDuplicate = false
      this.roDuplicateMessage = ''
      this.roCheckSeq++
      this.roChecking = false
    },
    // Asked when the RO changes and again before submit. The API re-checks on confirm regardless, so a
    // failure here is non-fatal: it just means the reviewer is not warned early.
    async checkRo() {
      this.roDuplicate = false
      this.roDuplicateMessage = ''
      const ro = this.estimate && this.estimate.repairOrderNumber
      const seq = ++this.roCheckSeq
      if (!ro || !String(ro).trim()) return
      this.roChecking = true
      try {
        const result = await this.checkRoNumber(String(ro).trim())
        if (seq !== this.roCheckSeq) return
        if (result && result.exists) {
          this.roDuplicate = true
          // Same wording as the API's own check (and the VIN-only page).
          this.roDuplicateMessage =
            `RO number ${String(ro).trim()} already exists for this shop in ADAS Map. ` +
            'Use a different RO number, or open the existing inspection.'
        }
      } catch (e) {
        // Leave it to the API's own check on confirm.
      } finally {
        if (seq === this.roCheckSeq) this.roChecking = false
      }
    },
    ...mapActions(customerStore, ['getShops']),
    onFileChange(e) { const f = e.target.files; this.file = f && f.length ? f[0] : null },
    onDrop(e) {
      this.dragActive = false
      const files = e.dataTransfer && e.dataTransfer.files
      if (!files || !files.length) return
      const f = files[0]
      if (f.type === 'application/pdf' || (f.name || '').toLowerCase().endsWith('.pdf')) this.file = f
    },
    setPdfUrl(url) { if (this.pdfUrl) URL.revokeObjectURL(this.pdfUrl); this.pdfUrl = url },
    apiError(e, fallback) {
      // Error envelope is { error: { code, message } }; tolerate a flat shape too.
      const d = e && e.response && e.response.data
      const err = (d && (d.error || d)) || null
      return (err && (err.message || err.Message)) || fallback
    },
    apiErrorCode(e) {
      const d = e && e.response && e.response.data
      const err = (d && (d.error || d)) || null
      return (err && (err.code || err.Code)) || null
    },
    // The dropdown binds insurer NAMES, so a parsed name that is not in the list leaves the select
    // showing nothing at all (no option matches the bound value). Normalise after every parse so the
    // control always has a valid selection: snap to the list on an exact match, keep unmatched text
    // as free entry rather than discarding what the parser found, and otherwise fall back to the
    // "(select insurer)" placeholder.
    matchInsurance() {
      if (!this.estimate) return
      const raw = (this.estimate.insuranceCompany || '').trim()
      if (!raw) {
        this.estimate.insuranceCompany = ''
        this.insuranceOverride = false
        return
      }
      const match = (this.insurers || []).find(
        (ins) => ((ins && ins.name) || '').trim().toLowerCase() === raw.toLowerCase()
      )
      if (match) {
        this.estimate.insuranceCompany = match.name
        this.insuranceOverride = false
      } else {
        this.insuranceOverride = true
      }
    },
    // Coming back off manual entry, the free text may not be a list option; re-match so the dropdown
    // never renders with nothing selected.
    onInsuranceOverrideToggle() {
      if (this.insuranceOverride) return
      // Coming OUT of manual entry: drop the hand-typed value rather than trying to match it back to
      // the list. Whatever was typed was, by definition, something the list did not offer.
      if (this.estimate) this.estimate.insuranceCompany = ''
      this.selectedInsurance = null
    },
    // A generic "failed to parse" tells a provider nothing they can act on. These say which of the
    // three things went wrong: too big, not a readable PDF, or the firm is not set up.
    parseErrorMessage(e) {
      const status = e && e.response && e.response.status
      if (status === 413) {
        return `That file is ${this.fileSizeText(this.file)}, over the ${this.maxUploadMb} MB limit. ` +
          'Re-export the estimate from CCC or Mitchell (an exported PDF is normally well under a megabyte) ' +
          'rather than printing or scanning it.'
      }
      if (status === 415 || status === 400 || status === 502 || status === 500) {
        return this.apiError(e,
          'That file could not be read as an estimate. It has to be the PDF exported by CCC or Mitchell - ' +
          'a scan, a photo or a file renamed to .pdf cannot be parsed.')
      }
      return this.apiError(e, 'Failed to parse the PDF.')
    },
    fileSizeText(file) {
      if (!file || !file.size) return 'too large'
      return `${(file.size / (1024 * 1024)).toFixed(1)} MB`
    },
    async parse() {
      if (!this.file) return
      // Checked here as well as by the API: a 30 MB round trip only to be refused is a poor way to
      // learn the file is the wrong one.
      const name = (this.file.name || '').toLowerCase()
      if (!name.endsWith('.pdf')) {
        this.errorMessage = 'Only PDF estimates can be uploaded. Export the estimate from CCC or Mitchell as a PDF and try again.'
        return
      }
      if (this.file.size > this.maxUploadMb * 1024 * 1024) {
        this.errorMessage = `That file is ${this.fileSizeText(this.file)}, over the ${this.maxUploadMb} MB limit. ` +
          'Re-export the estimate from CCC or Mitchell rather than printing or scanning it.'
        return
      }
      this.busy = true; this.parsing = true; this.errorMessage = ''; this.notConfigured = false; this.submittedOk = false
      this.overlayHidden = false
      try {
        const result = await this.parsePdf({ file: this.file, source: this.source, quality: this.quality })
        this.blobKey = result.blobKey
        this.fileName = result.fileName
        this.contentType = result.contentType
        this.byteSize = result.byteSize
        this.estimate = result.estimate
        this.lines = (result.estimate && result.estimate.lines) || []
        this.matchInsurance()
        this.setPdfUrl(URL.createObjectURL(this.file))
      } catch (e) {
        this.errorMessage = this.parseErrorMessage(e)
        this.notConfigured = this.apiErrorCode(e) === 'firm_not_configured' ||
          (e && e.response && e.response.status === 422)
      } finally {
        this.busy = false; this.parsing = false
      }
    },
    addLine() { this.lines.push({ lineHeaderDesc: '', lineDesc: '', laborOperation: '', supplementCode: '', partNumber: '', quantity: null, price: null, laborHours: null, paintHours: null, manualEntry: true }) },
    removeLine(i) { this.lines.splice(i, 1) },
    // Re-checks the RO before showing the review, so a duplicate is caught on the page rather than in
    // the dialog. The API checks again on confirm regardless.
    async openSummary() {
      if (!this.canConfirm) return
      this.errorMessage = ''
      await this.checkRo()
      if (this.roDuplicate) {
        this.errorMessage = this.roDuplicateMessage
        return
      }
      this.showSummary = true
    },
    async confirmSubmission() {
      if (!this.canConfirm) return
      this.busy = true; this.errorMessage = ''
      this.overlayHidden = false
      try {
        this.estimate.lines = this.lines
        const created = await this.confirmPdf({
          source: this.source, blobKey: this.blobKey, fileName: this.fileName,
          contentType: this.contentType, byteSize: this.byteSize, estimate: this.estimate,
          // Stripped off by the API before forwarding; ADAS Map has no field for it.
          customerShop: this.shop && this.shop.shopName ? this.shop : null
        })
        // Pick up a shop that was created by this submission so the next upload can reuse it.
        try { this.shops = await this.getShops() } catch (e) { /* autocomplete only */ }
        this.showSummary = false
        this.submitted = created || null
        this.submittedOk = true
        this.reset()
      } catch (e) {
        this.errorMessage = this.apiError(e, 'Failed to submit the estimate.')
      } finally {
        this.busy = false
      }
    },
    reset() {
      this.roDuplicate = false; this.roDuplicateMessage = ''; this.roChecking = false
      this.showSummary = false
      this.file = null; this.estimate = null; this.lines = []; this.blobKey = null
      this.shop = emptyShop()
      this.errorMessage = ''; this.notConfigured = false
      this.insuranceOverride = false; this.overlayHidden = false
      this.setPdfUrl(null)
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    }
  }
}
</script>
<style lang="scss" scoped>
.col-supp { width: 70px; }
.custom-label { font-size: 0.75rem; text-transform: uppercase; color: #6c757d; margin-bottom: 2px; }
.custom-pdf-frame { width: 100%; height: 70vh; border: 1px solid #dee2e6; }
// The line grid renders at its FULL height (the pane grows with the line count and the page scrolls)
// because collapsing it to the window height meant constant scrolling inside a short box. The section
// header collapses it instead. The PDF pane sticks so it stays beside the rows.
@media (min-width: 992px) {
  .custom-pdf-frame { position: sticky; top: 1rem; }
  .custom-review-foot { padding-top: 0.5rem; }
}
// Column headers stay in view against the page scroll.
.custom-lines thead th { position: sticky; top: 0; z-index: 1; background-color: #fff; }
.custom-lines-section {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  margin-top: 0.75rem;
}
.custom-section-header {
  width: 100%;
  text-align: left;
  background: #f8f9fa;
  border: 0;
  border-bottom: 1px solid #dee2e6;
  padding: 0.4rem 0.6rem;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
}
.custom-section-hint { float: right; font-weight: 400; color: #8a949e; }
.custom-pane-link { float: right; font-size: 0.8rem; font-weight: 400; }
.custom-pane-restore { margin-bottom: 0.5rem; font-size: 0.8rem; font-weight: 400; }
// The two review panes. Stacks below lg, side by side above it.
.custom-split { display: flex; flex-wrap: wrap; width: 100%; }
.custom-split-pane { width: 100%; min-width: 0; }
@media (min-width: 992px) {
  // gap gives the PDF and the grid breathing room; flex:1 shares what is left evenly, so the gap is
  // subtracted automatically and a collapsed PDF pane hands its whole width over with no extra rule.
  .custom-split { flex-wrap: nowrap; gap: 1em; }
  .custom-split-pane { flex: 1 1 0; width: auto; }
}
.custom-field-error {
  font-size: 0.75rem;
  color: #b02a37;
  margin-top: 2px;
}
.custom-field-note {
  font-size: 0.75rem;
  color: #6c757d;
  margin-top: 2px;
}
.custom-summary {
  dt { font-weight: 600; color: #495057; }
  dd { margin-bottom: 0.5rem; }
}
.custom-vehicle-section {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  margin-top: 0.75rem;
}
.custom-vehicle-body {
  padding: 0.5rem 0.6rem;
}
// Stands in for the PDF embed while it is hidden (see the note on the object tag).
.custom-pdf-placeholder {
  display: flex; align-items: center; justify-content: center;
  height: 200px; border: 1px solid #dee2e6; border-radius: 4px;
  background-color: #f7f8fa; color: #8a949e; font-size: 0.85rem;
}
.custom-lines { font-size: 0.85rem; }
.custom-lines td, .custom-lines th { vertical-align: middle; }
.custom-lines .col-num { width: 44px; }
.custom-lines .col-oper { width: 90px; }
.custom-lines .col-part { width: 130px; }
.custom-lines .col-qty { width: 70px; }
.custom-lines .col-price { width: 90px; }
.custom-lines .col-hrs { width: 80px; }
.custom-lines .col-rm { width: 44px; }
// Group and Description get real room; a narrow pane scrolls sideways rather than squeezing them.
.custom-lines .col-group { min-width: 170px; }
.custom-lines .col-desc { min-width: 280px; }
.custom-lines-scroll { overflow-x: auto; }
.custom-notes { font-size: 0.8rem; color: #6c757d; padding-left: 1.1rem; }
.custom-dropzone {
  border: 2px dashed #b8c2cc; border-radius: 8px; padding: 2.5rem 1rem; text-align: center;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  color: #6c757d; cursor: pointer; transition: border-color 0.15s ease, background-color 0.15s ease;
}
.custom-dropzone:hover, .custom-dropzone-active { border-color: #0d6efd; background-color: rgba(13,110,253,0.05); color: #0d6efd; }
.custom-dropzone-icon { font-size: 2rem; margin-bottom: 0.5rem; }
// Issue 622: keep the upload-bar controls at one height (40px) so this page matches
// the ADAS Map / Blueprint upload pages. Scoped to the bar so the dense line-grid
// inputs below keep their own size.
.custom-controls {
  .form-select-sm,
  .form-control-sm {
    height: 40px;
    min-height: 40px;
  }
  // Match the Parse / Clear buttons to the 40px control height.
  .btn {
    height: 40px;
    display: inline-flex;
    align-items: center;
  }
}
</style>
